import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Drawer, useMediaQuery } from "@mui/material";
import moment from "moment";
import ChatList from "./components/ChatList";
import ChatHeader from "./components/ChatHeader";
import ChatContent from "./components/ChatContent";
import { Message } from "./components/Message";
import { Chat } from "./components/ChatItem";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { sendMessageSagaAction, fetchChatHistorySagaAction, fetchIndividualChatHistorySagaAction } from "../../../redux/saga/chatbot/saga-actions";
import { chatbotSliceAction } from "../../../redux/slices/chatbot";
import { ChatSession, ChatHistoryConversation } from "../../../redux/slices/chatbot/Types";
import "./Chatbot.scss";

interface ChatData {
  id: string;
  messages: Message[];
  title: string;
  createdAt: Date;
  lastMessageTime?: Date;
}

// Helper function to transform API session data to ChatData format
const transformSessionToChatData = (session: ChatSession): ChatData => {
  const utcDate = moment.utc(session.timestamp).toDate();
  return {
    id: session.conversation_session_id,
    messages: [], // Messages will be empty initially, can be fetched later if needed
    title: session.message,
    createdAt: utcDate,
    lastMessageTime: utcDate,
  };
};

// Helper function to transform individual chat history API response to Message[] format
const transformChatHistoryToMessages = (conversations: ChatHistoryConversation[]): Message[] => {
  const messages: Message[] = [];

  // Process each conversation
  conversations.forEach((conversation) => {
    // Process question and response pairs
    conversation.question.forEach((question) => {
      // Find corresponding response by message_id
      const response = conversation.generated_response.find(
        (resp) => resp.message_id === question.message_id
      );

      // Add user message (question) with unique ID including conversation_id
      const questionTimestamp = moment.utc(question.timestamp).toDate();
      messages.push({
        id: `user-${conversation.conversation_id}-${question.message_id}`,
        text: question.query,
        sender: "user",
        timestamp: questionTimestamp,
      });

      // Add assistant message (response) if available, with unique ID
      if (response) {
        const responseTimestamp = moment.utc(response.timestamp).toDate();
        messages.push({
          id: `assistant-${conversation.conversation_id}-${response.message_id}`,
          text: response.answer,
          sender: "assistant",
          timestamp: responseTimestamp,
        });
      }
    });
  });

  // Sort messages by timestamp to maintain chronological order
  // Use a stable sort to preserve order of messages with identical timestamps
  return messages.sort((a, b) => {
    const timeDiff = a.timestamp.getTime() - b.timestamp.getTime();
    // If timestamps differ, sort by time
    if (Math.abs(timeDiff) > 0) {
      return timeDiff;
    }
    // If timestamps are identical (or very close), ensure user message comes before assistant message
    // This handles cases where question and response have the same timestamp
    if (a.sender === "user" && b.sender === "assistant") {
      return -1;
    }
    if (a.sender === "assistant" && b.sender === "user") {
      return 1;
    }
    // If same sender and same timestamp, maintain original order (stable sort)
    return 0;
  });
};

const PartnerChatbot: React.FC = () => {
  const dispatch = useAppDispatch();
  const chatbotState = useAppSelector((state) => state.chatbot);
  const [chats, setChats] = useState<ChatData[]>([]); // Chat history
  const [currentChatMessages, setCurrentChatMessages] = useState<Message[]>([]); // Current session messages
  const [activeChatId, setActiveChatId] = useState<string | null>(null); // For selecting from history
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Fetch chat history on component mount
  useEffect(() => {
    dispatch(fetchChatHistorySagaAction());
  }, [dispatch]);

  // Update chats state from Redux chatHistory when it changes
  useEffect(() => {
    if (chatbotState.chatHistory) {
      // Flatten grouped history into single array, maintaining order: today, yesterday, last_7_days, older
      const allSessions: ChatSession[] = [
        ...chatbotState.chatHistory.today,
        ...chatbotState.chatHistory.yesterday,
        ...chatbotState.chatHistory.last_7_days,
        ...chatbotState.chatHistory.older,
      ];
      
      const transformedChats = allSessions.map(transformSessionToChatData);
      setChats(transformedChats);
    }
  }, [chatbotState.chatHistory]);

  const activeChat = useMemo(
    () => chats.find((chat) => chat.id === activeChatId),
    [chats, activeChatId]
  );

  // Use current chat messages if no active chat from history, otherwise use history chat messages
  const messages = activeChatId && activeChat ? activeChat.messages : currentChatMessages;

  // Handle chatbot API response
  useEffect(() => {
    if (chatbotState.messageResponse) {
      // Parse API timestamp as UTC (API returns UTC timestamps)
      // Use moment.utc() to explicitly treat the timestamp as UTC
      const utcDate = moment.utc(chatbotState.messageResponse.timestamp).toDate();
      
      const assistantMessage: Message = {
        id: chatbotState.messageResponse.message_id || `assistant-${Date.now()}`,
        text: chatbotState.messageResponse.answer,
        sender: "assistant",
        timestamp: utcDate,
      };

      // If activeChatId exists, update the chat in history
      if (activeChatId && activeChat) {
        setChats((prev) =>
          prev.map((chat) => {
            if (chat.id !== activeChatId) return chat;
            
            // Find and replace loading message
            const hasLoadingMessage = chat.messages.some((msg) => msg.id.startsWith("loading-"));
            const updatedMessages = hasLoadingMessage
              ? chat.messages.map((msg) =>
                  msg.id.startsWith("loading-") ? assistantMessage : msg
                )
              : [...chat.messages, assistantMessage];

            return {
              ...chat,
              messages: updatedMessages,
              lastMessageTime: new Date(),
            };
          })
        );
      } else {
        // Update current chat messages (not in history)
        setCurrentChatMessages((prev) => {
          const hasLoadingMessage = prev.some((msg) => msg.id.startsWith("loading-"));
          return hasLoadingMessage
            ? prev.map((msg) =>
                msg.id.startsWith("loading-") ? assistantMessage : msg
              )
            : [...prev, assistantMessage];
        });
      }

      // Reset the message response after processing
      dispatch(chatbotSliceAction.setMessageResponse(null));
    }
  }, [chatbotState.messageResponse, activeChatId, activeChat, dispatch]);

  // Handle errors
  useEffect(() => {
    if (chatbotState.status.error && chatbotState.status.errorMessage) {
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        text: `Error: ${chatbotState.status.errorMessage}`,
        sender: "assistant",
        timestamp: new Date(),
      };

      // If activeChatId exists, update the chat in history
      if (activeChatId && activeChat) {
        setChats((prev) =>
          prev.map((chat) => {
            if (chat.id !== activeChatId) return chat;
            
            // Replace loading message with error message
            const updatedMessages = chat.messages.map((msg) =>
              msg.id.startsWith("loading-") ? errorMessage : msg
            );

            return {
              ...chat,
              messages: updatedMessages,
              lastMessageTime: new Date(),
            };
          })
        );
      } else {
        // Update current chat messages (not in history)
        setCurrentChatMessages((prev) =>
          prev.map((msg) =>
            msg.id.startsWith("loading-") ? errorMessage : msg
          )
        );
      }

      // Reset error state
      dispatch(chatbotSliceAction.setError({ error: false, errorMessage: null }));
    }
  }, [chatbotState.status.error, chatbotState.status.errorMessage, activeChatId, activeChat, dispatch]);

  // Handle individual chat history API response
  useEffect(() => {
    if (chatbotState.individualChatHistory && activeChatId) {
      // Verify that the conversation_id matches the activeChatId
      // The conversation_id should match the conversation_session_id (activeChatId)
      const conversationMatches = chatbotState.individualChatHistory.some(
        (conv) => conv.conversation_id === activeChatId
      );
      
      // Only process if the conversation matches the active chat
      if (conversationMatches) {
        // Transform the chat history to messages
        const messages = transformChatHistoryToMessages(chatbotState.individualChatHistory);
        
        // Update the chat in chats state with the loaded messages
        setChats((prev) =>
          prev.map((chat) =>
            chat.id === activeChatId
              ? {
                  ...chat,
                  messages: messages,
                }
              : chat
          )
        );
      }

      // Reset the individual chat history after processing (or if it doesn't match)
      dispatch(chatbotSliceAction.setIndividualChatHistory(null));
    }
  }, [chatbotState.individualChatHistory, activeChatId, dispatch]);

  const chatsForList: Chat[] = useMemo(
    () =>
      chats.map((chat) => ({
        id: chat.id,
        title: chat.title,
        lastMessage: chat.messages[chat.messages.length - 1]?.text,
        lastMessageTime: chat.lastMessageTime,
      })),
    [chats]
  );

  // Transform grouped chat history for ChatList component
  const groupedChatsForList = useMemo(() => {
    if (!chatbotState.chatHistory) {
      return {
        today: [],
        yesterday: [],
        last_7_days: [],
        older: [],
      };
    }

    const transformGroup = (sessions: ChatSession[]): Chat[] => {
      return sessions.map((session) => {
        const chatData = transformSessionToChatData(session);
        return {
          id: chatData.id,
          title: chatData.title,
          lastMessage: chatData.messages[chatData.messages.length - 1]?.text,
          lastMessageTime: chatData.lastMessageTime,
        };
      });
    };

    return {
      today: transformGroup(chatbotState.chatHistory.today),
      yesterday: transformGroup(chatbotState.chatHistory.yesterday),
      last_7_days: transformGroup(chatbotState.chatHistory.last_7_days),
      older: transformGroup(chatbotState.chatHistory.older),
    };
  }, [chatbotState.chatHistory]);

  const handleNewChat = useCallback(() => {
    // Reset current chat - clear messages and active chat
    setCurrentChatMessages([]);
    setActiveChatId(null);
    // Reset chatbot Redux state to clear any pending responses
    dispatch(chatbotSliceAction.resetChatbotState());
    // Close drawer on mobile when new chat is created
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile, dispatch]);

  const handleChatSelect = useCallback(
    (chatId: string) => {
      // Clear current chat messages when switching chats
      setCurrentChatMessages([]);
      
      // Clear any pending individual chat history to prevent mixing messages
      dispatch(chatbotSliceAction.setIndividualChatHistory(null));
      
      setActiveChatId(chatId);
      
      // Find the selected chat
      const selectedChat = chats.find((chat) => chat.id === chatId);
      
      // If chat exists but has no messages loaded, fetch individual chat history
      if (selectedChat && selectedChat.messages.length === 0) {
        dispatch(fetchIndividualChatHistorySagaAction(chatId));
      }
      
      // Close drawer on mobile when chat is selected
      if (isMobile) {
        setDrawerOpen(false);
      }
    },
    [isMobile, chats, dispatch]
  );

  const handleSendMessage = useCallback(
    (text: string) => {
      // Create UTC timestamp to match API response format
      const utcTimestamp = new Date(new Date().toISOString());
      
      // Add user message immediately
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        text,
        sender: "user",
        timestamp: utcTimestamp,
      };

      // Add loading message immediately
      const loadingMessageId = `loading-${Date.now()}`;
      const loadingMessage: Message = {
        id: loadingMessageId,
        text: "...",
        sender: "assistant",
        timestamp: utcTimestamp,
      };

      // If activeChatId exists, update the chat in history
      if (activeChatId && activeChat) {
        setChats((prev) =>
          prev.map((chat) =>
            chat.id === activeChatId
              ? {
                  ...chat,
                  messages: [...chat.messages, userMessage, loadingMessage],
                  lastMessageTime: new Date(),
                }
              : chat
          )
        );
      } else {
        // Add to current chat messages (not in history)
        setCurrentChatMessages((prev) => [...prev, userMessage, loadingMessage]);
      }

      // Dispatch saga action to send message to API
      dispatch(sendMessageSagaAction(text));
    },
    [activeChatId, activeChat, dispatch]
  );

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const chatContent = (
    <div className="chatbot-container">
      {!isMobile && (
        <div className="chatbot-sidebar">
          <ChatList
            chats={chatsForList}
            groupedChats={groupedChatsForList}
            activeChatId={activeChatId || undefined}
            onChatSelect={handleChatSelect}
            onNewChat={handleNewChat}
            onRefresh={() => dispatch(fetchChatHistorySagaAction())}
            isLoading={chatbotState.chatHistoryStatus.loading}
          />
        </div>
      )}
      <div className="chatbot-main">
        <ChatHeader
          title={activeChat?.title || "AI Assistant"}
          onMenuClick={handleDrawerOpen}
          showMenuButton={isMobile}
        />
        <ChatContent
          messages={messages}
          onSendMessage={handleSendMessage}
          isLoadingHistory={chatbotState.individualChatHistoryStatus.loading && !!activeChatId}
        />
      </div>
    </div>
  );

  // Desktop view: full-width with sidebar
  if (!isMobile) {
    return <div className="chatbot-wrapper">{chatContent}</div>;
  }

  // Mobile/Tablet view: Full page with drawer for chat list
  return (
    <>
      <div className="chatbot-wrapper">{chatContent}</div>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            width: "280px",
            maxWidth: "85vw",
          },
        }}
      >
        <ChatList
          chats={chatsForList}
          groupedChats={groupedChatsForList}
          activeChatId={activeChatId || undefined}
          onChatSelect={handleChatSelect}
          onNewChat={handleNewChat}
          onRefresh={() => dispatch(fetchChatHistorySagaAction())}
          isLoading={chatbotState.chatHistoryStatus.loading}
        />
      </Drawer>
    </>
  );
};

export default PartnerChatbot;
